import { DialogPrompt } from '@app/components/dialog-prompt/dialog-prompt';
import { AngleLeftIcon } from '@app/components/icons/angle-left';
import { CheckmarkNewIcon } from '@app/components/icons/checkmark-new';
import { CheckmarkVeryBoldIcon } from '@app/components/icons/checkmark-very-bold';
import { CloseNewIcon } from '@app/components/icons/close-new';
import { Copy as CopyIcon } from '@app/components/icons/copy';
import { EditNewIcon } from '@app/components/icons/edit-new';
import { HourGlass } from '@app/components/icons/hour-glass';
import { Redo } from '@app/components/icons/redo';
import { Save } from '@app/components/icons/save';
import { ShareWithArrowIcon } from '@app/components/icons/share-with-arrow';
import { Spinner } from '@app/components/icons/spinner';
import { TrashNewIcon } from '@app/components/icons/trash-new';
import { Undo } from '@app/components/icons/undo';
import { Tooltip } from '@app/components/tooltip';
import { Alert } from '@app/routes/apps/components/toast/icons/alert';
import { buildTitleFromDate } from '@app/routes/content/utils';
import { getPathParams } from '@app/routes/utils';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ShareMenu } from '../../../../components/share-menu';
import { buildCloneEditRequest } from '../../../../domain';
import {
  useRecordingAnalytics,
  useSaveState,
  useUndoRedo,
} from '../../../../hooks';
import {
  selectActiveAdvancedEdit,
  selectActiveEditDraft,
  selectActiveEditStatus,
  selectAdvancedEditList,
  selectAdvancedEditsState,
  selectColorPresets,
  selectIsEditFrozen,
  selectRecording,
  selectRecordingBackUrl,
  selectRecordingTitleEditState,
} from '../../../../selector';
import { formatCaptionsForPut } from '../../../../utils';

interface EditorHeaderProps {
  mode: 'recording' | 'edit';
  // REMOVE_BEFORE_EDITOR_V2_RELEASE: temporary slot for the editor version
  // toggle. Drop this prop once V1 is removed.
  versionToggle?: React.ReactNode;
}

// ── Shared internal hook ──────────────────────────────────────────────────────

/**
 * Manages inline title editing for both header modes.
 *
 * mode === 'recording':
 *   - displayTitle = buildTitleFromDate(recording)
 *   - save → dispatch [recording] update title started
 *   - titleEditState tracks the API call status (shows spinner while saving)
 *
 * mode === 'edit':
 *   - displayTitle = activeEdit.draft.title
 *   - save → dispatch [advanced-editor] edit draft changed (feeds auto-save debounce)
 *   - titleEditState is idle — the EditAutoSaveStatus icon reflects the save state
 */
const useTitleEdit = (mode: 'recording' | 'edit', frozen = false) => {
  const dispatch = useAppDispatch();
  const recording = useAppSelector(selectRecording);
  const titleEditState = useAppSelector(selectRecordingTitleEditState);
  const activeEdit = useAppSelector(selectActiveAdvancedEdit);
  const currentDraft = useAppSelector(selectActiveEditDraft);
  const { trackRecordingEvent } = useRecordingAnalytics();
  const { t } = useTranslation();

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const editTitle = activeEdit?.draft.title ?? '';

  const displayTitle =
    mode === 'edit'
      ? editTitle
      : buildTitleFromDate({
          locale: 'en',
          title: recording?.title,
          startedAt: recording?.started_at || '',
        });

  const handleTitleSave = useCallback(() => {
    const title = inputRef.current?.value?.trim();
    if (!title || frozen) return;

    if (mode === 'edit' && activeEdit?.id && currentDraft) {
      // Edit title → feed the existing auto-save debounce pipeline.
      // EditAutoSaveStatus already reflects the resulting save state.
      dispatch({
        type: '[advanced-editor] edit draft changed',
        payload: {
          editId: activeEdit.id,
          draft: { ...currentDraft, title },
          previousDraft: currentDraft,
        },
      });
      setIsEditingTitle(false);
    } else if (recording?.id) {
      trackRecordingEvent('ch_recording_editor_title_updated');
      dispatch({
        type: '[recording] update title started',
        payload: { recordingId: recording.id, title },
      });
    }
  }, [
    frozen,
    mode,
    activeEdit,
    currentDraft,
    dispatch,
    recording,
    trackRecordingEvent,
  ]);

  const handleTitleCancel = useCallback(() => setIsEditingTitle(false), []);

  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleTitleSave();
      else if (e.key === 'Escape') handleTitleCancel();
    },
    [handleTitleSave, handleTitleCancel],
  );

  // Close the input when a recording title save completes
  useEffect(() => {
    if (mode === 'recording' && titleEditState.status === 'success') {
      setIsEditingTitle(false);
    }
  }, [mode, titleEditState.status]);

  // Pre-fill the input when it opens
  useEffect(() => {
    if (isEditingTitle && inputRef.current) {
      inputRef.current.value =
        mode === 'edit' ? editTitle : recording?.title || '';
      inputRef.current.focus();
    }
  }, [isEditingTitle, mode, editTitle, recording?.title]);

  // In edit mode there is no separate API loading state — the EditAutoSaveStatus
  // icon already communicates the save result, so pass an idle sentinel.
  const effectiveTitleEditState =
    mode === 'edit' ? { status: 'idle' as const } : titleEditState;

  return {
    t,
    recording,
    titleEditState: effectiveTitleEditState,
    isEditingTitle,
    setIsEditingTitle,
    inputRef,
    displayTitle,
    handleTitleSave,
    handleTitleCancel,
    handleTitleKeyDown,
    frozen,
  };
};

// ── Shared inner component ────────────────────────────────────────────────────

const EditableTitle = ({
  t,
  recording,
  titleEditState,
  isEditingTitle,
  setIsEditingTitle,
  inputRef,
  displayTitle,
  handleTitleSave,
  handleTitleCancel,
  handleTitleKeyDown,
  frozen,
}: ReturnType<typeof useTitleEdit>) => (
  <div className="flex items-center gap-2 mx-4 flex-1 min-w-0 h-8">
    {isEditingTitle ? (
      <input
        ref={inputRef}
        type="text"
        defaultValue={recording?.title || ''}
        onKeyDown={handleTitleKeyDown}
        aria-label={t('content-recording.editor-v2.recording-title-input')}
        className="text-xl font-medium text-spring-neutral-b0 bg-transparent border border-gray-650 rounded px-1 focus:outline-none focus:border-spring-primary w-full h-full"
      />
    ) : (
      <span className="text-xl font-medium text-spring-neutral-b2 truncate h-full flex items-center">
        {displayTitle}
      </span>
    )}
    {isEditingTitle ? (
      <div className="flex items-center gap-2 flex-shrink-0">
        {titleEditState.status === 'processing' ? (
          <Spinner size="tiny" theme="dark" />
        ) : (
          <button
            onClick={handleTitleSave}
            aria-label={t('content-recording.editor-v2.save-title')}
            className="flex items-center justify-center text-white hover:opacity-80 transition-opacity"
          >
            <CheckmarkNewIcon className="size-5" aria-hidden="true" />
          </button>
        )}
        <button
          onClick={handleTitleCancel}
          aria-label={t('content-recording.editor-v2.cancel-title-edit')}
          className="flex items-center justify-center text-white hover:opacity-80 transition-opacity"
        >
          <CloseNewIcon className="size-5" aria-hidden="true" />
        </button>
      </div>
    ) : frozen ? null : (
      <button
        onClick={() => setIsEditingTitle(true)}
        aria-label={t('content-recording.editor-v2.edit-title')}
        className="flex items-center justify-center text-white flex-shrink-0 hover:opacity-80 transition-opacity"
      >
        <EditNewIcon className="size-5" aria-hidden="true" />
      </button>
    )}
  </div>
);

/**
 * Formats an ISO timestamp to a medium-style datetime string.
 * Uses the browser locale — consistent with formatDate() in content/utils.ts.
 * e.g. "Jan 1, 2024, 3:45 PM"
 */
const formatSaveDateTime = (iso: string): string => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return '';
  }
};

const EditAutoSaveStatus = () => {
  const { t } = useTranslation();
  const { isSaving, isSaved, isFailed, status, updatedAt, failureReason } =
    useSaveState();

  const tooltipText = (() => {
    if (isSaving) {
      return t('content-recording.edit-list.tooltip-saving');
    }
    if (isFailed) {
      return failureReason === 'conflict'
        ? t('content-recording.edit-list.save-conflict')
        : t('content-recording.edit-list.tooltip-failed-generic');
    }
    // saved or idle — show server-confirmed timestamp when available
    if (updatedAt) {
      return t('content-recording.edit-list.tooltip-updated-at', {
        datetime: formatSaveDateTime(updatedAt),
      });
    }
    return null;
  })();

  const icon = (
    <div className="relative flex items-center justify-center gap-2 size-9">
      <Save className="size-5 text-white" />
      <div className="absolute bottom-0 right-0 size-4">
        {isSaving && (
          <HourGlass className="size-4 text-orange-apricot animate-spinCustom" />
        )}
        {(isSaved || status === 'idle') && (
          <CheckmarkVeryBoldIcon className="size-4 text-green-400" />
        )}
        {isFailed && <Alert className="size-4 text-red-400" />}
      </div>
    </div>
  );

  if (!tooltipText) return icon;

  return (
    <Tooltip tooltip={tooltipText} placement="bottom-center" shouldShow={true}>
      {icon}
    </Tooltip>
  );
};

// ── RecordingModeHeader ───────────────────────────────────────────────────────

const RecordingModeHeader = ({
  // REMOVE_BEFORE_EDITOR_V2_RELEASE: temporary editor version toggle slot.
  versionToggle,
}: {
  versionToggle?: React.ReactNode;
}) => {
  const { org_id } = getPathParams(['org_id']);
  const backUrl = useAppSelector(selectRecordingBackUrl);
  const titleProps = useTitleEdit('recording');
  const { t } = titleProps;

  return (
    <header className="h-16 shrink-0 flex flex-row items-center gap-5 px-6 bg-spring-neutral-base border-b border-spring-neutral-b0/10 overflow-hidden">
      {/* Left: Back to library pill */}
      <a
        href={backUrl ?? `/org/${org_id}/content/library`}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-spring-neutral-b4/90 hover:bg-spring-neutral-b4 transition-colors shrink-0"
      >
        <AngleLeftIcon
          className="w-4 h-4 text-spring-neutral-b0"
          aria-hidden="true"
        />
        <span className="text-xs font-medium text-spring-neutral-b2 whitespace-nowrap">
          {t('content-recording.editor-v2.back-to-library')}
        </span>
      </a>

      {/* Center: Editable title */}
      <EditableTitle {...titleProps} />

      {/* Right: optional version toggle + ShareMenu */}
      <div className="flex items-center gap-3 shrink-0">
        {/* TODO: Add Undo/Redo buttons here - wire up useUndoRedo() hook - EVE-14275 */}
        {/* REMOVE_BEFORE_EDITOR_V2_RELEASE: drop the {versionToggle} slot. */}
        {versionToggle}
        <ShareMenu />
      </div>
    </header>
  );
};

// ── EditModeHeader ────────────────────────────────────────────────────────────

const EditModeHeader = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { editId: routeEditId } = useParams<{ editId: string }>();
  const isFrozen = useAppSelector(selectIsEditFrozen);
  const editStatus = useAppSelector(selectActiveEditStatus);
  const titleProps = useTitleEdit('edit', isFrozen);
  const { t, recording } = titleProps;
  const { trackRecordingEvent } = useRecordingAnalytics();

  const activeEdit = useAppSelector(selectActiveAdvancedEdit);
  const advancedEditsState = useAppSelector(selectAdvancedEditsState);
  const editList = useAppSelector(selectAdvancedEditList);
  const colorPresets = useAppSelector(selectColorPresets);

  // Fix 6 — useRef pattern for activeEditDraft so handleDone never captures a
  // stale closure value while also avoiding activeEditDraft in its dep array.
  const activeEditDraft = useAppSelector(selectActiveEditDraft);
  const activeEditDraftRef = useRef(activeEditDraft);
  useEffect(() => {
    activeEditDraftRef.current = activeEditDraft;
  }, [activeEditDraft]);

  const { undo, redo, canUndo, canRedo, hasUnsavedChanges } = useUndoRedo();

  const [isBackConfirmOpen, setIsBackConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const wasCloningRef = useRef(false);
  const isCreatingEdit = advancedEditsState.status === 'processing';
  const canClone = !!recording?.id && !!activeEdit;

  const handleClone = useCallback(() => {
    if (!recording?.id || !activeEdit || isCreatingEdit) return;
    wasCloningRef.current = true;
    trackRecordingEvent('ch_recording_editor_clip_cloned');
    const cloneRequest = buildCloneEditRequest(
      activeEdit,
      recording.id,
      editList.map(r => r.title),
    );
    dispatch({
      type: '[advanced-editor] create edit requested',
      payload: {
        recordingId: recording.id,
        request: {
          ...cloneRequest,
          captions: formatCaptionsForPut(
            activeEdit.draft.captions,
            colorPresets,
          ),
        },
      },
    });
  }, [
    colorPresets,
    dispatch,
    editList,
    activeEdit,
    isCreatingEdit,
    recording?.id,
    trackRecordingEvent,
  ]);

  // `../edits/:id` — v6's `..` is route-relative, not one URL segment.
  useEffect(() => {
    if (!wasCloningRef.current) return;
    if (advancedEditsState.status !== 'success') return;
    const newEditId = advancedEditsState.activeEditId;
    if (!newEditId || newEditId === routeEditId) {
      wasCloningRef.current = false;
      return;
    }
    wasCloningRef.current = false;
    navigate(`../edits/${newEditId}`);
  }, [
    advancedEditsState.activeEditId,
    advancedEditsState.status,
    routeEditId,
    navigate,
  ]);

  const handleDeleteConfirmed = useCallback(() => {
    if (!activeEdit?.id || !recording?.id) return;
    setIsDeleteConfirmOpen(false);
    navigate('../');
    dispatch({
      type: '[advanced-editor] delete edit requested',
      payload: { editId: activeEdit.id, recordingId: recording.id },
    });
  }, [dispatch, navigate, activeEdit?.id, recording?.id]);

  const handleBackToVideo = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsBackConfirmOpen(true);
    } else {
      navigate('../');
    }
  }, [navigate, hasUnsavedChanges]);

  const handleBackConfirmed = useCallback(() => {
    setIsBackConfirmOpen(false);
    navigate('../');
  }, [navigate]);

  const handleBackCancelled = useCallback(() => {
    setIsBackConfirmOpen(false);
  }, []);

  const handleExportEdit = useCallback(() => {
    if (!activeEdit?.id || !recording?.id || isFrozen) return;
    dispatch({
      type: '[advanced-editor] export edit requested',
      payload: { editId: activeEdit.id, recordingId: recording.id },
    });
  }, [dispatch, activeEdit?.id, recording?.id, isFrozen]);

  return (
    <header className="h-16 shrink-0 flex flex-row items-center gap-5 px-6 bg-spring-neutral-base border-b border-spring-neutral-b0/10 overflow-hidden">
      {/* Left: Back to video + Edit badge */}
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={handleBackToVideo}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-spring-neutral-b4/90 hover:bg-spring-neutral-b4 transition-colors"
        >
          <AngleLeftIcon
            className="w-4 h-4 text-spring-neutral-b0"
            aria-hidden="true"
          />
          <span className="text-xs font-medium text-spring-neutral-b2 whitespace-nowrap">
            {t('content-recording.editor-v2.back-to-video')}
          </span>
        </button>
        {/* TODO: Replace border-[#73CB87] with spring design token once mapped — EVE-14275 */}
        <span className="px-2 py-0.5 rounded-lg border border-[#73CB87] text-xs font-medium text-white whitespace-nowrap">
          {t('content-recording.editor-v2.edit-badge')}
        </span>
      </div>

      {/* Center: Editable title */}
      <EditableTitle {...titleProps} />

      {/* Right: Clone + Delete + Undo + Redo + Export */}
      <div className="flex items-center gap-3 shrink-0">
        {/* Delete — disabled when frozen (export in-flight or completed) */}
        <button
          onClick={() => setIsDeleteConfirmOpen(true)}
          disabled={isFrozen}
          aria-label={t('content-recording.editor-v2.delete-edit')}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-spring-neutral-b0 hover:bg-spring-neutral-b0/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <TrashNewIcon className="w-5 h-5" aria-hidden="true" />
        </button>

        {/* Undo / Redo — disabled when frozen */}
        <button
          onClick={undo}
          disabled={!canUndo || isFrozen}
          aria-label={t('content-recording.editor-v2.undo')}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-spring-neutral-b0 hover:bg-spring-neutral-b0/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Undo className="w-5 h-5" aria-hidden="true" />
        </button>
        <button
          onClick={redo}
          disabled={!canRedo || isFrozen}
          aria-label={t('content-recording.editor-v2.redo')}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-spring-neutral-b0 hover:bg-spring-neutral-b0/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Redo className="w-5 h-5" aria-hidden="true" />
        </button>

        <EditAutoSaveStatus />

        {/* Clone — always enabled regardless of frozen state */}
        <button
          onClick={handleClone}
          disabled={isCreatingEdit || !canClone}
          aria-label={t('content-recording.editor-v2.clone-edit')}
          className="flex items-center gap-2 pl-4 pr-2 py-2 border border-solid border-gray-600 rounded-10 text-sm font-medium text-spring-neutral-b0 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {t('content-recording.editor-v2.clone-edit')}
          <CopyIcon className="w-4 h-4" aria-hidden="true" />
        </button>

        {/* Export button — 3 states based on edit status:
            DRAFT / FAILED  → enabled "Export" button
            PROCESSING      → disabled "Exporting..." with spinner
            COMPLETED       → hidden (export is permanent, no re-export) */}
        {editStatus !== 'COMPLETED' && (
          <button
            onClick={handleExportEdit}
            disabled={editStatus === 'PROCESSING'}
            aria-label={t('content-recording.editor-v2.export-edit')}
            className="flex items-center gap-2 pl-4 pr-2 py-2 border border-solid border-gray-600 rounded-10 text-sm font-medium text-spring-neutral-b0 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {editStatus === 'PROCESSING' ? (
              <>
                {t('content-recording.editor-v2.export-edit-processing')}
                <Spinner size="tiny" theme="dark" />
              </>
            ) : (
              <>
                {t('content-recording.editor-v2.export-edit')}
                <ShareWithArrowIcon className="w-5 h-5" aria-hidden="true" />
              </>
            )}
          </button>
        )}
      </div>

      {/* Unsaved changes dialog */}
      <DialogPrompt
        isShown={isBackConfirmOpen}
        isDarkTheme={true}
        onClose={handleBackCancelled}
        onConfirm={handleBackConfirmed}
        title={t('content-recording.editor-v2.unsaved-changes-title')}
        description={t(
          'content-recording.editor-v2.unsaved-changes-description',
        )}
        confirmText={t('content-recording.editor-v2.unsaved-changes-confirm')}
        cancelText={t('common.cancel')}
      />

      {/* Delete confirmation dialog */}
      <DialogPrompt
        isShown={isDeleteConfirmOpen}
        isDarkTheme={true}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirmed}
        title={t('content-recording.editor-v2.delete-edit-title')}
        description={t('content-recording.editor-v2.delete-edit-description')}
        confirmText={t('content-recording.editor-v2.delete-edit-confirm')}
        cancelText={t('common.cancel')}
      />
    </header>
  );
};

// ── Public API — thin mode switcher ──────────────────────────────────────────

export const EditorHeader = ({ mode, versionToggle }: EditorHeaderProps) => {
  if (mode === 'edit') return <EditModeHeader />;
  // REMOVE_BEFORE_EDITOR_V2_RELEASE: drop the `versionToggle` pass-through.
  return <RecordingModeHeader versionToggle={versionToggle} />;
};
